import React, { useCallback, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'src/store';
import { getUserManagementGeneralUiState } from 'src/app/admin/views/user-management/state/userManagementSelector';
import { ControlledConfirmDialog } from 'src/shared/components/confirm/ControlledConfirmDialog';
import SnackbarAlert from 'src/shared/components/snackbar-alert/SnackbarAlert';
import Modal from 'src/shared/components/modal/Modal';
import styles from './UserManagementModals.module.scss';
import { reset, updateGeneralUiState } from '../../state/userManagementSlice';
import NewProjectUser from '../new-project-user/NewProjectUser';
import { EditProjectUser, type EditUserUnsavedWorkState } from '../edit-project-user/EditProjectUser';
import { ViewProjectUser } from '../view-project-user/ViewProjectUser';

/**
 * Hosts the three user-management modals (add, edit, view) and the global success snackbar.
 * Edit modal adds unsaved-change and in-flight-save guards around `EditProjectUser`.
 */
const defaultUnsavedWorkState: EditUserUnsavedWorkState = {
    hasUnsavedChanges: false,
    sitePermissionsSaveInProgress: false,
    profileSaveInProgress: false,
};

/** Which blocking dialog to show when the user tries to close the edit modal */
type EditUserPendingCloseDialog = 'none' | 'unsaved' | 'waitSave';

/** Wraps `EditProjectUser` with modal close handling: discard confirmation or wait for saves */
const EditUserModalWithCloseGuard = () => {
    const dispatch = useAppDispatch();
    const [unsavedWorkState, setUnsavedWorkState] = useState<EditUserUnsavedWorkState>(defaultUnsavedWorkState);
    const [pendingCloseDialog, setPendingCloseDialog] = useState<EditUserPendingCloseDialog>('none');

    /** Avoids re-renders when `EditProjectUser` reports the same aggregate flags */
    const handleUnsavedWorkStateChange = useCallback((next: EditUserUnsavedWorkState) => {
        setUnsavedWorkState(prev =>
            prev.hasUnsavedChanges === next.hasUnsavedChanges &&
            prev.sitePermissionsSaveInProgress === next.sitePermissionsSaveInProgress &&
            prev.profileSaveInProgress === next.profileSaveInProgress
                ? prev
                : next
        );
    }, []);

    const handleEditModalClose = () => {
        if (unsavedWorkState.sitePermissionsSaveInProgress || unsavedWorkState.profileSaveInProgress) {
            setPendingCloseDialog('waitSave');
            return;
        }
        if (unsavedWorkState.hasUnsavedChanges) {
            setPendingCloseDialog('unsaved');
            return;
        }
        dispatch(reset());
    };

    return (
        <>
            <Modal classNames={{ body: styles.newUserModal }} onClose={handleEditModalClose} dataTestId='user-management-modal-edit-user'>
                <EditProjectUser onUnsavedWorkStateChange={handleUnsavedWorkStateChange} />
            </Modal>
            <ControlledConfirmDialog
                open={pendingCloseDialog === 'unsaved'}
                onOpenChange={open => {
                    if (!open) {
                        setPendingCloseDialog('none');
                    }
                }}
                variant='confirm'
                title='Discard unsaved changes?'
                description='You have unsaved changes. If you close now, your changes will be lost.'
                confirmLabel='Close without saving'
                cancelLabel='Keep editing'
                onConfirm={() => {
                    dispatch(reset());
                    setPendingCloseDialog('none');
                }}
            />
            <ControlledConfirmDialog
                open={pendingCloseDialog === 'waitSave'}
                onOpenChange={open => {
                    if (!open) {
                        setPendingCloseDialog('none');
                    }
                }}
                variant='alert'
                title='Saving in progress'
                description='Please wait until saving finishes before closing this window.'
                confirmLabel='OK'
                onConfirm={() => setPendingCloseDialog('none')}
            />
        </>
    );
};

const UserManagementModals = () => {
    const dispatch = useAppDispatch();
    const generalUiState = useAppSelector(getUserManagementGeneralUiState);

    const resetForm = () => {
        dispatch(reset());
    };

    return (
        <div>
            {generalUiState.shouldShowNewUserModal && (
                <Modal classNames={{ body: styles.newUserModal }} onClose={resetForm} dataTestId='user-management-modal-add-user'>
                    <NewProjectUser />
                </Modal>
            )}
            {generalUiState.shouldShowEditUserModal && <EditUserModalWithCloseGuard />}

            {generalUiState.shouldShowViewUserModal && (
                <Modal classNames={{ body: styles.newUserModal }} onClose={resetForm} dataTestId='user-management-modal-view-user'>
                    <ViewProjectUser />
                </Modal>
            )}

            {generalUiState.shouldShowSuccessAlert && (
                <SnackbarAlert
                    dataTestId='user-management-success-snackbar'
                    message={generalUiState.successStatusMessage}
                    open={generalUiState.shouldShowSuccessAlert}
                    onClose={() => dispatch(updateGeneralUiState({ shouldShowSuccessAlert: false }))}
                    autoHideDuration={6000}
                />
            )}
        </div>
    );
};

export default UserManagementModals;
